import { FC, ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import GridViewIcon from "@mui/icons-material/GridView";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { ScoringProfilePills } from "@/lib/scoring/components/ScoringProfilePills";
import { ScoringSourcePills } from "@/lib/scoring/components/ScoringSourcePills";
import { useFeatureFlag } from "@/hooks/useLaunchDarkly";
import type { ScoringProfile } from "@/lib/scoring/types";

export type ExploreViewMode = "list" | "matrix";

interface ScoringExploreTopbarProps {
  scoringEnabled: boolean;
  onCreateNewProfile: () => void;
  onEditProfile: (profile: ScoringProfile) => void;
  viewMode: ExploreViewMode;
  onViewModeChange: (v: ExploreViewMode) => void;
  /** Whether the Matrix view is offered. Hidden while only advanced (V2) scoring is active. */
  matrixAvailable?: boolean;
  compareMode: boolean;
  onToggleCompare: () => void;
  canCompare: boolean;
  /** Search field rendered at the start of the toolbar. */
  searchSlot?: ReactNode;
  /** Filters control (button + popover) rendered after the search. */
  filtersSlot?: ReactNode;
}

/**
 * Single-row explore toolbar: search + filters on the left, the highlighted
 * scoring zone and the List/Matrix toggle on the right. Kept to one row so the
 * companies table gets the maximum vertical space.
 */
export const ScoringExploreTopbar: FC<ScoringExploreTopbarProps> = ({
  scoringEnabled,
  onCreateNewProfile,
  onEditProfile,
  viewMode,
  onViewModeChange,
  matrixAvailable = true,
  compareMode,
  onToggleCompare,
  canCompare,
  searchSlot,
  filtersSlot,
}) => {
  const scoringV2Enabled = useFeatureFlag("maScoringModelsV2", false);
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ px: 1, py: 0.5, rowGap: 1, flexWrap: "wrap" }}
    >
      {searchSlot && (
        <Box sx={{ flex: "0 0 auto", width: { xs: "100%", sm: 280 } }}>
          {searchSlot}
        </Box>
      )}

      {filtersSlot}

      <Box sx={{ flex: 1 }} />

      {scoringEnabled &&
        (scoringV2Enabled && viewMode === "list" ? (
          // V2 on + list view: merged legacy+model picker. The matrix is V1-only,
          // so it keeps the original profile pills below.
          <ScoringSourcePills
            onCreateNew={onCreateNewProfile}
            onEditProfile={onEditProfile}
          />
        ) : (
          <ScoringProfilePills
            onCreateNew={onCreateNewProfile}
            onEditProfile={onEditProfile}
            mode={
              viewMode === "matrix"
                ? compareMode && canCompare
                  ? "compare"
                  : "matrix"
                : "list"
            }
          />
        ))}

      {matrixAvailable && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, v) => v && onViewModeChange(v)}
        >
          <ToggleButton
            value="list"
            data-testid="view-list-btn"
            sx={{ px: 1.5 }}
          >
            <FormatListBulletedIcon fontSize="small" sx={{ mr: 0.5 }} />
            List
          </ToggleButton>
          <ToggleButton
            value="matrix"
            data-testid="view-matrix-btn"
            sx={{ px: 1.5 }}
          >
            <GridViewIcon fontSize="small" sx={{ mr: 0.5 }} />
            Matrix
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {matrixAvailable && viewMode === "matrix" && (
        <Tooltip
          title={canCompare ? "" : "Apply two profiles to compare"}
          placement="top"
        >
          <span>
            <Button
              variant={compareMode ? "contained" : "outlined"}
              color="primary"
              startIcon={<CompareArrowsIcon />}
              disabled={!canCompare}
              onClick={onToggleCompare}
              data-testid="compare-toggle-btn"
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              Compare
            </Button>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
};
